/**
 * The colour vocabulary for personal Well notes.
 *
 * Extracted from WellMyNotes because the scripture card now draws in these
 * colours too — a verse carrying a note is underlined in that note's colour, so
 * the meaning a member builds up ("amber is a key insight") reads the same in
 * both places. Two copies of this list would drift into two vocabularies.
 */

// Each colour carries a soft meaning so members can build a quick mental
// shorthand. Indigo is the default; "custom" opens the native colour picker.
export const NOTE_COLORS = [
  { value: '#4F46E5', key: 'indigo'   }, // reflection
  { value: '#F59E0B', key: 'amber'    }, // key insight
  { value: '#E11D48', key: 'rose'     }, // conviction
  { value: '#10B981', key: 'emerald'  }, // action / commitment
  { value: '#0EA5E9', key: 'sky'      }, // question to study
  { value: '#8B5CF6', key: 'violet'   }, // prayer
  { value: '#71717A', key: 'stone'    }, // misc / archive
];

export const DEFAULT_NOTE_COLOR = NOTE_COLORS[0].value;

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export function normalizeColor(c) {
  if (typeof c !== 'string' || !HEX_RE.test(c)) return null;
  return c.toUpperCase();
}

export function colorForRender(c) {
  return normalizeColor(c) || DEFAULT_NOTE_COLOR;
}

// ── Keeping a custom colour visible ────────────────────────────────────────
//
// The composer offers a native colour picker, so a member can choose pure white
// or pure black. As a swatch on a note that is merely ugly; as a 2px underline
// under scripture it is invisible — in light mode for the pale end, in dark mode
// for the dark end, and the member is left with a note they cannot find.
//
// Clamping luminance into a mid band makes one value work in both themes without
// the component needing to know which is active (it would not be reactive to the
// dark-mode toggle anyway). All seven curated colours already sit inside the
// band and pass through unchanged — only an extreme custom pick moves.
// The underline sits on --card-bg, which is #FFFFFF in light mode and #1C1C1E
// (luminance ~0.11) in dark. The band is the range that stays visible on both.
// All seven curated colours fall inside it — amber, the lightest, is 0.66.
const MIN_L = 0.28;
const MAX_L = 0.68;

function toRgb(hex) {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
}

// Perceived luminance (ITU-R BT.601). Good enough for "can I see this line",
// and cheaper than the sRGB-linearized form for a decision this coarse.
function luminance([r, g, b]) {
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function mix([r, g, b], [tr, tg, tb], amount) {
  const m = (a, t) => Math.round(a + (t - a) * amount);
  return `#${[m(r, tr), m(g, tg), m(b, tb)]
    .map((v) => v.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

/** A note's colour, adjusted only if it would be invisible in one of the themes. */
export function accentForMarking(c) {
  const hex = colorForRender(c);
  const rgb = toRgb(hex);
  const l   = luminance(rgb);

  // Mixed only far enough to reach the boundary, never past it. Solving
  // `l * (1 - a) = MAX_L` and `l + a * (1 - l) = MIN_L` for the mix amount is
  // what keeps white from inverting all the way to black — it should darken to
  // the edge of visible, not become the opposite colour.
  if (l > MAX_L) return mix(rgb, [0, 0, 0], 1 - MAX_L / l);
  if (l < MIN_L) return mix(rgb, [255, 255, 255], (MIN_L - l) / (1 - l));
  return hex;
}
